if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        if (theme.settings.afterAtc !== 'no-js') {
          this.addEventListener('submit', this.handleSubmit.bind(this));
        }

        const productSection = this.closest('.js-product');
        if (productSection && !productSection.querySelector('variant-picker')) {
          this.initFallbackBuyNowButtons(productSection);
          this.initFallbackFixedFooterButton();
        }
      }
    }

    initFallbackBuyNowButtons(productSection) {
      const bindButtons = () => {
        const buttons = [
          this.querySelector('#main-buy-now-btn'),
          document.querySelector('#footer-buy-now-btn')
        ].filter(Boolean);

        buttons.forEach((button) => {
          if (button.__bound) return;
          button.__bound = true;
          this.bindFallbackBuyNow(button, productSection);
        });
      };

      bindButtons();

      if (this.fallbackBuyNowObserver) return;

      this.fallbackBuyNowObserver = new MutationObserver(() => bindButtons());
      this.fallbackBuyNowObserver.observe(productSection, {
        childList: true,
        subtree: true
      });
    }

    bindFallbackBuyNow(button, productSection) {
      button.addEventListener('click', async (evt) => {
        evt.preventDefault();
        evt.stopPropagation();

        if (button.getAttribute('aria-disabled') === 'true' || button.disabled) return;

        button.setAttribute('aria-disabled', 'true');
        button.classList.add('is-loading');

        try {
          const formData = new FormData(this.form);
          const mainVariantId = formData.get('id');
          const quantityInput = productSection.querySelector('quantity-input input[type="number"]');
          const mainQuantity = parseInt(quantityInput?.value || formData.get('quantity') || '1', 10);
          const recommendedProducts = window.RecommendedProducts?.getSelectedProductsForCart() || [];
          const careItems = window.CareUpsell?.getSelectedItemsForCart
            ? (window.CareUpsell.getSelectedItemsForCart() || [])
            : [];
          const globoItems = this.getGloboProductionIdArr().map((id) => ({ id, quantity: 1 }));
          const items = [
            { id: mainVariantId, quantity: Number.isFinite(mainQuantity) && mainQuantity > 0 ? mainQuantity : 1 },
            ...globoItems,
            ...recommendedProducts,
            ...careItems
          ];

          if (!mainVariantId) throw new Error('Failed to find product variant');

          await this.bulkAddToCartWithRecommended(items);

          if (window.RecommendedProducts) {
            window.RecommendedProducts.clearSelection();
          }
          if (window.CareUpsell) {
            window.CareUpsell.clearSelection();
          }

          window.location.href = '/checkout';
        } catch (error) {
          console.error('Buy now failed:', error);
          this.setErrorMsgState(error.message || 'Failed to proceed to checkout');
        } finally {
          button.classList.remove('is-loading');
          button.removeAttribute('aria-disabled');
        }
      });
    }

    initFallbackFixedFooterButton() {
      const bindButton = () => {
        const button = document.querySelector('#fixed-footer-btn');
        if (!button || button.__bound) return;

        button.__bound = true;
        this.bindFallbackFixedFooterAtc(button);
      };

      bindButton();

      if (this.fallbackFixedFooterObserver) return;

      this.fallbackFixedFooterObserver = new MutationObserver(() => bindButton());
      this.fallbackFixedFooterObserver.observe(document.body, {
        childList: true,
        subtree: true
      });
    }

    bindFallbackFixedFooterAtc(button) {
      button.addEventListener('click', (evt) => {
        evt.preventDefault();
        evt.stopPropagation();

        if (button.getAttribute('aria-disabled') === 'true' || button.disabled) return;
        if (!this.form || !this.submitBtn) return;
        if (this.submitBtn.getAttribute('aria-disabled') === 'true' || this.submitBtn.disabled) return;

        this.syncLoadingState(this.submitBtn, button);

        if (typeof this.form.requestSubmit === 'function') {
          this.form.requestSubmit();
        } else {
          this.submitBtn.click();
        }
      });
    }

    syncLoadingState(sourceButton, targetButton) {
      targetButton.setAttribute('aria-disabled', 'true');
      targetButton.classList.add('is-loading');

      const cleanup = () => {
        targetButton.classList.remove('is-loading');
        targetButton.removeAttribute('aria-disabled');
        observer.disconnect();
      };

      const observer = new MutationObserver(() => {
        const isSourceLoading = sourceButton.classList.contains('is-loading')
          || sourceButton.getAttribute('aria-disabled') === 'true';

        if (!isSourceLoading) cleanup();
      });

      observer.observe(sourceButton, {
        attributes: true,
        attributeFilter: ['class', 'aria-disabled']
      });

      setTimeout(() => {
        const isSourceLoading = sourceButton.classList.contains('is-loading')
          || sourceButton.getAttribute('aria-disabled') === 'true';

        if (!isSourceLoading) cleanup();
      }, 0);
    }

    getGloboProductionIdArr() {
      const hiddenInputs = document.querySelector('.gpo-hidden-inputs');
      if (!hiddenInputs) return [];

      return Array.from(hiddenInputs.children).reduce((result, input) => {
        const variantIds = input.getAttribute('gpo-data-variant-id');
        if (!variantIds) return result;
        return result.concat(variantIds.split(','));
      }, []);
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      // 【核心修改】检查是否有选中的推荐产品和 Care 服务
      const recommendedProducts = window.RecommendedProducts?.getSelectedProductsForCart() || [];
      const careItems = window.CareUpsell?.getSelectedItemsForCart ? (window.CareUpsell.getSelectedItemsForCart() || []) : [];

      if (recommendedProducts.length > 0 || careItems.length > 0) {
        // 如果有推荐产品，使用批量加购
        const formData = new FormData(this.form);
        const mainVariantId = formData.get('id');
        const mainQuantity = parseInt(formData.get('quantity') || '1');

        // 组合所有产品：主产品 + 推荐产品
        let allItems = [{ id: mainVariantId, quantity: mainQuantity }];
        allItems = allItems.concat(recommendedProducts);
        allItems = allItems.concat(careItems);

        // 调用批量加购
        try {
          await this.bulkAddToCartWithRecommended(allItems);

          // 清空推荐产品选择
          if (window.RecommendedProducts) {
            window.RecommendedProducts.clearSelection();
          }
          // 清空 Care 选择
          if (window.CareUpsell) {
            window.CareUpsell.clearSelection();
          }

          // 【新增】触发 footer 价格更新
          // clearSelection() 内部已经会调用 updateFooterPrice()
          // 但为了确保更新，这里再次显式调用
          const variantPicker = document.querySelector('variant-picker');
          if (variantPicker && typeof variantPicker.updateFixedFooterPrice === 'function') {
            setTimeout(() => variantPicker.updateFixedFooterPrice(), 100);
          }
        } catch (error) {
          console.error('Bulk add to cart failed:', error);
          this.setErrorMsgState(error.message || 'Failed to add to cart');
        } finally {
          // Re-enable "Add to Cart" button.
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.removeAttribute('aria-disabled');
        }
        return;
      }

      // 【原有逻辑】如果没有推荐产品，使用原有单个加购
      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const cartState = window.theme?.cartState || window.CartState;
        const cachedCartData = cartState && typeof cartState.peek === 'function' ? cartState.peek() : null;

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();

        if (data.status) this.setErrorMsgState(data.description);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          if (this.cartDrawer) {
            this.cartDrawer.renderContents(
              data,
              !quickAddDrawer && theme.settings.afterAtc === 'drawer'
            );
          } else if (window.location.pathname === theme.routes.cart) {
            const cartItems = document.querySelector('cart-items');
            if (cartItems) cartItems.refreshCart();
          }
        }

        this.postAddToCartTasks({
          cartState,
          cachedCartData,
          formData,
          addResponseData: data
        }).catch(() => {});
      } catch (error) {
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg.textContent
          }
        }));
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    async postAddToCartTasks({ cartState, cachedCartData, formData, addResponseData }) {
      if (!addResponseData || !addResponseData.variant_id) return;

      if (cartState && typeof cartState.invalidate === 'function') cartState.invalidate();
      let newCartData;
      if (cartState) {
        newCartData = await cartState.get({ force: true });
      } else {
        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) return;
        newCartData = await newCartResponse.json();
      }

      const newItem = newCartData?.items?.find((item) => item.variant_id === addResponseData.variant_id);
      if (!newItem) return;

      const requestedQuantity = parseInt(formData.get('quantity') || '1', 10);
      const cachedItem = cachedCartData?.items?.find((item) => item.variant_id === addResponseData.variant_id);
      const oldQuantity = cachedItem ? cachedItem.quantity : Math.max(0, (newItem.quantity || 0) - requestedQuantity);
      const newQuantity = newItem.quantity || oldQuantity;
      const quantity = Math.max(0, newQuantity - oldQuantity);
      const product = {
        price: newItem.price / 100,
        "index": 0,
        "item_id": newItem.id,
        "item_name": newItem.title,
        "item_brand": 'Snapmaker',
        "price": newItem.price / 100,
        quantity,
        ...arrayToCategoryObject(newItem.variant_options)
      }

      if (oldQuantity > 0) {
        this.dispatchEvent(new CustomEvent('on:line-item:change', {
          bubbles: true,
          detail: {
            method: 'handleSubmit',
            cart: newCartData,
            variantId: addResponseData.variant_id,
            oldQuantity,
            newQuantity
          }
        }));
        return;
      }

      const quickAddDrawerEl = this.closest('.quick-add__footer');
      const module = !!quickAddDrawerEl ? 'quick add drawer' : 'buy buttons';
      if (typeof logAddToCart === 'function') {
        logAddToCart([product], 'handleSubmit', product.price * quantity, module);
      }
      this.dispatchEvent(new CustomEvent('on:cart:add', {
        bubbles: true,
        detail: {
          cart: newCartData,
          variantId: addResponseData.variant_id
        }
      }));
    }

    /**
     * 批量添加到购物车（主产品 + 推荐产品）
     * @param {Array} items - [{id: variantId, quantity: number}]
     */
    async bulkAddToCartWithRecommended(items) {
      try {
        let sections = 'cart-icon-bubble';
        if (this.cartDrawer) {
          sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
        }

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify({
            items: items,
            sections: sections,
            sections_url: window.location.pathname,
          })
        });

        const data = await response.json();

        if (data.status && data.status !== 'success') {
          throw new Error(data.description || 'Add to cart failed');
        }

        const cartState = window.theme?.cartState || window.CartState;
        if (cartState && typeof cartState.invalidate === 'function') cartState.invalidate();

        // 更新购物车图标
        ProductForm.updateCartIcon(data);

        // 打开购物车抽屉
        if (this.cartDrawer) {
          setTimeout(() => this.cartDrawer.renderContents(data, theme.settings.afterAtc === 'drawer'), 700);
        }

        // 触发 GTM 埋点（如果有）
        if (typeof logAddToCart === 'function') {
          const gtmItems = items.map((item, index) => {
            const cartItem = data.items?.find(ci => ci.variant_id == item.id);
            return {
              index: index,
              item_id: item.id,
              item_name: cartItem?.title || '',
              item_brand: 'Snapmaker',
              price: cartItem ? cartItem.price / 100 : 0,
              quantity: item.quantity
            };
          });

          const totalPrice = gtmItems.reduce((sum, item) =>
            sum + (item.price * item.quantity), 0
          );

          logAddToCart(gtmItems, 'Add', totalPrice, 'product-page-with-recommended');
        }

        return data;
      } catch (error) {
        console.error('Bulk add to cart failed:', error);
        throw error;
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) this.errorMsg.textContent = error;
    }
  }

  customElements.define('product-form', ProductForm);
}
